import React, { useState } from 'react';
import { useConversationsByTicket, useConversationsByTask, createConversation, Conversation, CreateConversationData } from '../useData';
import ConversationThread from './ConversationThread';
import MessageInput from './MessageInput';
import { Button, ConfirmationDialog } from '../../../components/ui';
import { useNotification } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

interface LinkedConversationsSectionProps {
  ticketId?: number;
  taskId?: number;
}

const LinkedConversationsSection: React.FC<LinkedConversationsSectionProps> = ({
  ticketId,
  taskId,
}) => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { colors } = useTheme();
  const [showReply, setShowReply] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Conversation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [conversationToDeleteId, setConversationToDeleteId] = useState<number | null>(null);

  const { conversations: ticketConversations, isLoading: isLoadingTicket, mutate: mutateTicket } = useConversationsByTicket(ticketId || 0);
  const { conversations: taskConversations, isLoading: isLoadingTask, mutate: mutateTask } = useConversationsByTask(taskId || 0);

  const conversations = ticketId ? ticketConversations : taskConversations;
  const isLoading = ticketId ? isLoadingTicket : isLoadingTask;
  const mutate = ticketId ? mutateTicket : mutateTask;

  const conversationType = ticketId ? 'TICKET' : 'TASK';

  const handleReply = (conversation: Conversation) => {
    setReplyingTo(conversation);
    setShowReply(true);
  };

  const handleSubmit = async (data: CreateConversationData) => {
    setIsSubmitting(true);
    try {
      await createConversation({
        ...data,
        ticketId: ticketId,
        taskId: taskId,
        replyToId: replyingTo?.id,
        staffUserId: data.senderType === 'STAFF' ? (data.staffUserId || user?.id) : undefined,
      });
      showSuccess('Message sent successfully');
      setShowReply(false);
      setReplyingTo(null);
      mutate();
    } catch (error: any) {
      showError(error?.message || 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      const { markAsRead } = await import('../useData');
      await markAsRead(id);
      mutate();
    } catch (error: any) {
      showError(error?.message || 'Failed to mark as read');
    }
  };

  const handleDeleteClick = (id: number) => {
    setConversationToDeleteId(id);
  };

  const handleDeleteConfirm = async () => {
    const id = conversationToDeleteId;
    if (id == null) return;
    setConversationToDeleteId(null);
    try {
      const { deleteConversation } = await import('../useData');
      await deleteConversation(id);
      showSuccess('Conversation deleted successfully');
      mutate();
    } catch (error: any) {
      showError(error?.message || 'Failed to delete conversation');
    }
  };

  if (!ticketId && !taskId) {
    return null;
  }

  return (
    <div className={cn('rounded-lg shadow p-6', colors.surface, colors.border, 'border')}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={cn('text-lg font-semibold', colors.text)}>
          Conversations ({conversations.length})
        </h3>
        {!showReply && (
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setReplyingTo(null);
              setShowReply(true);
            }}
          >
            New Message
          </Button>
        )}
      </div>

      {showReply && (
        <div className="mb-6">
          <MessageInput
            conversationType={conversationType}
            ticketId={ticketId}
            taskId={taskId}
            replyToId={replyingTo?.id}
            senderType="STAFF"
            staffUserId={user?.id}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowReply(false);
              setReplyingTo(null);
            }}
            isLoading={isSubmitting}
          />
        </div>
      )}

      {isLoading ? (
        <p className={cn('text-sm', colors.textMuted)}>Loading conversations...</p>
      ) : conversations.length === 0 ? (
        <p className={cn('text-sm', colors.textMuted)}>No conversations yet.</p>
      ) : (
        <ConversationThread
          conversations={conversations}
          onReply={handleReply}
          onMarkAsRead={handleMarkAsRead}
          onDelete={handleDeleteClick}
          currentUserId={user?.id}
        />
      )}

      <ConfirmationDialog
        isOpen={conversationToDeleteId !== null}
        onClose={() => setConversationToDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete conversation"
        message="Are you sure you want to delete this conversation?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};

export default LinkedConversationsSection;
